import React from 'react';
import { 
  Flex, 
  Icon, 
  Box, 
  Text, 
  FlexProps 
} from '@chakra-ui/react';
import { Brain } from 'lucide-react';

// Methodist color palette
const methodistBlue = '#0066CC';
const methodistNavy = '#002D72';

interface JIMIWellnessLogoProps extends FlexProps {
  /**
   * Size variant for the logo
   * - 'sm': Small size for compact spaces
   * - 'md': Medium size for normal usage
   * - 'lg': Large size for prominent display
   */
  size?: 'sm' | 'md' | 'lg';
  
  /**
   * Whether to show the tagline "For Healthcare Professionals"
   */
  showTagline?: boolean;
  
  /**
   * Whether to show the logo horizontally (default) or vertically
   */
  orientation?: 'horizontal' | 'vertical';
}

/**
 * A reusable component for the JIMI Wellness logo with optional tagline
 */
const JIMIWellnessLogo: React.FC<JIMIWellnessLogoProps> = ({ 
  size = 'md', 
  showTagline = true,
  orientation = 'horizontal',
  ...flexProps
}) => {
  // Size mappings
  const sizeMap = {
    sm: {
      iconSize: 5,
      titleSize: 'md',
      subtitleSize: 'sm',
      taglineSize: '2xs',
      iconMargin: 1.5,
    },
    md: {
      iconSize: 7,
      titleSize: 'xl',
      subtitleSize: 'lg',
      taglineSize: 'xs',
      iconMargin: 2,
    },
    lg: {
      iconSize: 9,
      titleSize: '2xl',
      subtitleSize: 'xl',
      taglineSize: 'sm',
      iconMargin: 3,
    },
  };

  const { iconSize, titleSize, subtitleSize, taglineSize, iconMargin } = sizeMap[size];

  return (
    <Flex 
      align="center" 
      role="img" 
      aria-label="JIMI Wellness Platform for Healthcare Professionals"
      flexDirection={orientation === 'vertical' ? 'column' : 'row'}
      {...flexProps}
    >
      <Icon 
        as={Brain} 
        boxSize={iconSize} 
        color={methodistBlue} 
        mr={orientation === 'horizontal' ? iconMargin : 0}
        mb={orientation === 'vertical' ? iconMargin : 0}
      />
      <Box textAlign={orientation === 'vertical' ? 'center' : 'left'}>
        <Text 
          fontWeight="medium" 
          fontSize={titleSize}
          color={methodistNavy} 
          lineHeight="0.9"
          letterSpacing="tighter"
          textTransform="uppercase"
          mb={0}
        >
          JIMI
        </Text>
        <Flex 
          alignItems="baseline"
          flexDirection={showTagline ? 'column' : 'row'}
          gap={showTagline ? 0.5 : 0}
        >
          <Text 
            fontWeight="medium"
            fontSize={subtitleSize}
            color={methodistNavy}
            lineHeight="0.9"
            letterSpacing="tight"
            mt={0}
          >
            Wellness
          </Text>
          {showTagline && (
            <Text 
              fontSize={taglineSize} 
              color={methodistBlue}
              fontWeight="normal"
              opacity={0.9}
              letterSpacing="tight"
              lineHeight="1"
            >
              For Healthcare Professionals
            </Text>
          )}
        </Flex>
      </Box>
    </Flex>
  );
};

export default JIMIWellnessLogo;
